import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Flame, Medal, TrendingUp, Crown, X, BarChart2, Zap, Award, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeaderboardPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [xpLeaderboard, setXpLeaderboard] = useState([]);
  const [xpLoading, setXpLoading] = useState(true);
  const [streakLeaders, setStreakLeaders] = useState([]);
  const [streakScope, setStreakScope] = useState("venue");
  const [period, setPeriod] = useState("week");
  const [testType, setTestType] = useState("full");
  const [scope, setScope] = useState("venue"); // default: My Venue
  const [xpScope, setXpScope] = useState("venue");
  const [xpPeriod, setXpPeriod] = useState("week");
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Staff performance popup
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffPerformance, setStaffPerformance] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(res => setCurrentUser(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (currentUser) fetchLeaderboard();
  }, [period, testType, scope, currentUser]);

  useEffect(() => {
    if (currentUser) fetchXpLeaderboard();
  }, [xpPeriod, xpScope, currentUser]);

  useEffect(() => {
    if (currentUser) fetchStreakLeaders();
  }, [streakScope, currentUser]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      let url = `${API}/leaderboard?period=${period}&test_type=${testType}`;
      if (scope === "venue" && currentUser?.venue_id) {
        url += `&venue_id=${currentUser.venue_id}`;
      } else if (currentUser?.org_id) {
        url += `&org_id=${currentUser.org_id}`;
      }
      const response = await axios.get(url);
      setLeaderboard(response.data);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStreakLeaders = async () => {
    try {
      let url = `${API}/leaderboard/login-streaks`;
      if (streakScope === "venue" && currentUser?.venue_id) {
        url += `?venue_id=${currentUser.venue_id}`;
      } else if (currentUser?.org_id) {
        url += `?org_id=${currentUser.org_id}`;
      }
      const response = await axios.get(url);
      setStreakLeaders(response.data);
    } catch (error) {
      console.error("Failed to fetch login-streak leaders:", error);
    }
  };

  const fetchXpLeaderboard = async () => {
    setXpLoading(true);
    try {
      let url = `${API}/leaderboard/xp?period=${xpPeriod}`;
      if (xpScope === "venue" && currentUser?.venue_id) {
        url += `&venue_id=${currentUser.venue_id}`;
      } else if (currentUser?.org_id) {
        url += `&org_id=${currentUser.org_id}`;
      }
      const response = await axios.get(url);
      setXpLeaderboard(response.data);
    } catch (error) {
      console.error("Failed to fetch XP leaderboard:", error);
    } finally {
      setXpLoading(false);
    }
  };

  const fetchStaffPerformance = async (staffId, staffName) => {
    setSelectedStaff({ id: staffId, name: staffName });
    setLoadingPerformance(true);
    try {
      const response = await axios.get(`${API}/user/${staffId}/public-profile`);
      setStaffPerformance(response.data);
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      setStaffPerformance(null);
    } finally {
      setLoadingPerformance(false);
    }
  };

  const closePerformancePopup = () => {
    setSelectedStaff(null);
    setStaffPerformance(null);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="text-[#C36A2F]" size={24} />;
      case 2:
        return <Medal className="text-gray-300" size={22} />;
      case 3:
        return <Medal className="text-amber-600" size={22} />;
      default:
        return <span className="text-lg font-bold text-[hsl(var(--muted-foreground))]">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return "bg-gradient-to-r from-[#C36A2F]/20 to-amber-500/10 border-[#C36A2F]/30";
      case 2:
        return "bg-gradient-to-r from-gray-400/20 to-gray-500/10 border-gray-400/30";
      case 3:
        return "bg-gradient-to-r from-amber-600/20 to-orange-500/10 border-amber-600/30";
      default:
        return "bg-[hsl(var(--secondary))]";
    }
  };

  return (
    <div data-testid="leaderboard-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Trophy className="text-[#C36A2F]" size={36} />
            Leaderboard
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Top performers and streak champions
          </p>
        </div>
      </div>

      <Tabs defaultValue="scores" className="space-y-6">
        <TabsList className="bg-[hsl(var(--secondary))] grid grid-cols-3 w-full sm:inline-flex sm:w-auto h-auto">
          <TabsTrigger value="scores" data-testid="tab-scores" className="text-xs sm:text-sm whitespace-normal px-1.5 py-2 sm:px-3">
            <Trophy size={16} className="mr-1 sm:mr-2 shrink-0" /> <span>Top Scores</span>
          </TabsTrigger>
          <TabsTrigger value="xp" data-testid="tab-xp" className="text-xs sm:text-sm whitespace-normal px-1.5 py-2 sm:px-3">
            <Zap size={16} className="mr-1 sm:mr-2 shrink-0" /> <span>XP Champions</span>
          </TabsTrigger>
          <TabsTrigger value="streaks" data-testid="tab-streaks" className="text-xs sm:text-sm whitespace-normal px-1.5 py-2 sm:px-3">
            <Flame size={16} className="mr-1 sm:mr-2 shrink-0" /> <span>Hot Streaks</span>
          </TabsTrigger>
        </TabsList>

        {/* Top Scores Tab */}
        <TabsContent value="scores" className="space-y-4">
          {/* Test Type Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-[hsl(var(--border))]">
            <button
              onClick={() => setTestType("full")}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold transition-colors ${
                testType === "full"
                  ? 'bg-[hsl(var(--primary))] text-black'
                  : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
              }`}
              data-testid="leaderboard-type-full"
            >
              Full Spec
            </button>
            <button
              onClick={() => setTestType("ingredients")}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold transition-colors ${
                testType === "ingredients"
                  ? 'bg-[hsl(var(--primary))] text-black'
                  : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
              }`}
              data-testid="leaderboard-type-ingredients"
            >
              Ingredients & Measurements
            </button>
          </div>

          {/* Scope Toggle: Venue vs Company */}
          <div className="flex rounded-lg overflow-hidden border border-[hsl(var(--border))]">
            <button
              onClick={() => setScope("company")}
              className={`flex-1 px-4 py-2 text-xs font-semibold transition-colors ${
                scope === "company"
                  ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] border-b-2 border-[hsl(var(--primary))]'
                  : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
              }`}
              data-testid="leaderboard-scope-company"
            >
              All Venues
            </button>
            <button
              onClick={() => setScope("venue")}
              className={`flex-1 px-4 py-2 text-xs font-semibold transition-colors ${
                scope === "venue"
                  ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] border-b-2 border-[hsl(var(--primary))]'
                  : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
              }`}
              data-testid="leaderboard-scope-venue"
            >
              My Venue
            </button>
          </div>

          {/* Period Filter — order: Week → Month → All Time */}
          <div className="flex gap-2">
            {[
              { value: "week", label: "This Week" },
              { value: "month", label: "This Month" },
              { value: "all", label: "All Time" }
            ].map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === p.value
                    ? 'bg-[hsl(var(--primary))] text-black'
                    : 'bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]'
                }`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-12 text-center text-[hsl(var(--muted-foreground))]">
              Loading leaderboard...
            </div>
          ) : leaderboard.length === 0 ? (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardContent className="py-12 text-center">
                <Trophy size={48} className="mx-auto text-[hsl(var(--muted-foreground))] mb-4" />
                <p className="text-[hsl(var(--muted-foreground))]">No results yet</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Complete some tests to see the leaderboard
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((entry) => (
                <div
                  key={entry.staff_id}
                  className={`p-4 rounded-xl border ${getRankBg(entry.rank)} cursor-pointer hover:opacity-90 transition-opacity`}
                  data-testid={`leaderboard-entry-${entry.rank}`}
                  onClick={() => entry.staff_id ? fetchStaffPerformance(entry.staff_id, entry.staff_name) : null}
                >
                  <div className="flex items-center gap-2 sm:gap-4">
                    <div className="w-8 sm:w-12 shrink-0 flex justify-center">
                      {getRankIcon(entry.rank)}
                    </div>

                    <div className="w-12 h-12 shrink-0 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                      <span className="text-[hsl(var(--primary))] text-lg font-bold">
                        {entry.staff_name?.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-lg truncate hover:text-[hsl(var(--primary))] transition-colors">
                        {entry.staff_name}
                      </p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] truncate">
                        {entry.total_tests} tests completed
                      </p>
                    </div>

                    {/* Stats inline on sm+ — moved below on phones so the name never shrinks to a letter */}
                    <div className="hidden sm:flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${
                          entry.avg_score >= 80 ? 'text-green-500' : 
                          entry.avg_score >= 60 ? 'text-[#C36A2F]' : 'text-red-500'
                        }`}>
                          {entry.avg_score?.toFixed(0)}%
                        </p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">avg score</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-[hsl(var(--primary))]">
                          {entry.best_score?.toFixed(0)}%
                        </p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">best</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex sm:hidden items-center justify-around mt-3 pt-3 border-t border-[hsl(var(--border))]/60">
                    <div className="text-center">
                      <p className={`text-xl font-bold ${
                        entry.avg_score >= 80 ? 'text-green-500' : 
                        entry.avg_score >= 60 ? 'text-[#C36A2F]' : 'text-red-500'
                      }`}>
                        {entry.avg_score?.toFixed(0)}%
                      </p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">avg score</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-[hsl(var(--primary))]">
                        {entry.best_score?.toFixed(0)}%
                      </p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">best</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* XP Champions Tab — pure XP from practice + tests, scoped to venue or company */}
        <TabsContent value="xp" className="space-y-4">
          {/* Scope Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-[hsl(var(--border))]">
            <button
              onClick={() => setXpScope("company")}
              className={`flex-1 px-4 py-2 text-xs font-semibold transition-colors ${
                xpScope === "company"
                  ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] border-b-2 border-[hsl(var(--primary))]'
                  : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
              }`}
              data-testid="xp-scope-company"
            >
              All Venues
            </button>
            <button
              onClick={() => setXpScope("venue")}
              className={`flex-1 px-4 py-2 text-xs font-semibold transition-colors ${
                xpScope === "venue"
                  ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] border-b-2 border-[hsl(var(--primary))]'
                  : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
              }`}
              data-testid="xp-scope-venue"
            >
              My Venue
            </button>
          </div>

          {/* Period Filter — order: Week → Month → All Time */}
          <div className="flex gap-2">
            {[
              { value: "week", label: "This Week" },
              { value: "month", label: "This Month" },
              { value: "all", label: "All Time" }
            ].map((p) => (
              <button
                key={p.value}
                onClick={() => setXpPeriod(p.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  xpPeriod === p.value
                    ? 'bg-[hsl(var(--primary))] text-black'
                    : 'bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]'
                }`}
                data-testid={`xp-period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {xpLoading ? (
            <div className="py-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="xp-loading">
              Loading XP leaderboard...
            </div>
          ) : xpLeaderboard.length === 0 ? (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardContent className="py-12 text-center" data-testid="xp-empty">
                <Zap size={48} className="mx-auto text-[hsl(var(--muted-foreground))] mb-4" />
                <p className="text-[hsl(var(--muted-foreground))]">No XP yet</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Complete practice sessions and tests to earn XP and climb the ranks
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="xp-leaderboard-list">
              {xpLeaderboard.map((entry) => (
                <div
                  key={entry.user_id}
                  className={`p-4 rounded-xl border ${getRankBg(entry.rank)} cursor-pointer hover:opacity-90 transition-opacity`}
                  data-testid={`xp-entry-${entry.rank}`}
                  onClick={() => entry.user_id ? fetchStaffPerformance(entry.user_id, entry.staff_name) : null}
                >
                  <div className="flex items-center gap-2 sm:gap-4">
                    <div className="w-8 sm:w-12 shrink-0 flex justify-center">
                      {getRankIcon(entry.rank)}
                    </div>

                    <div className="w-12 h-12 shrink-0 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                      <span className="text-[hsl(var(--primary))] text-lg font-bold">
                        {entry.staff_name?.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-lg truncate hover:text-[hsl(var(--primary))] transition-colors">
                        {entry.staff_name}
                      </p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                        Lvl {entry.level} &middot; {entry.level_title}
                      </p>
                    </div>

                    <div className="hidden sm:block text-right shrink-0">
                      <p className="text-2xl font-bold text-[#C36A2F] flex items-center gap-1 justify-end">
                        <Zap size={20} />
                        {entry.total_xp.toLocaleString()}
                      </p>
                      <p className="text-[10px] text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                        {entry.challenge_count} practised &middot; {entry.test_count} tests
                      </p>
                    </div>
                  </div>

                  <div className="flex sm:hidden items-center justify-between mt-3 pt-3 border-t border-[hsl(var(--border))]/60">
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      {entry.challenge_count} practised &middot; {entry.test_count} tests
                    </p>
                    <p className="text-lg font-bold text-[#C36A2F] flex items-center gap-1">
                      <Zap size={16} />
                      {entry.total_xp.toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Streaks Tab — daily login streaks */}
        <TabsContent value="streaks" className="space-y-4">
          {/* Scope Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-[hsl(var(--border))]">
            <button
              onClick={() => setStreakScope("company")}
              className={`flex-1 px-4 py-2 text-xs font-semibold transition-colors ${
                streakScope === "company"
                  ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] border-b-2 border-[hsl(var(--primary))]'
                  : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
              }`}
              data-testid="streak-scope-company"
            >
              All Venues
            </button>
            <button
              onClick={() => setStreakScope("venue")}
              className={`flex-1 px-4 py-2 text-xs font-semibold transition-colors ${
                streakScope === "venue"
                  ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] border-b-2 border-[hsl(var(--primary))]'
                  : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
              }`}
              data-testid="streak-scope-venue"
            >
              My Venue
            </button>
          </div>

          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="text-orange-500" size={24} />
                Longest Login Streaks
              </CardTitle>
            </CardHeader>
            <CardContent>
              {streakLeaders.length === 0 ? (
                <div className="py-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="streak-empty">
                  No active streaks yet — log in tomorrow to start one!
                </div>
              ) : (
                <div className="space-y-3" data-testid="streak-list">
                  {streakLeaders.map((entry) => (
                    <div
                      key={entry.user_id || entry.staff_id}
                      className={`flex items-center gap-4 p-4 rounded-lg ${getRankBg(entry.rank)} cursor-pointer hover:opacity-90 transition-opacity`}
                      data-testid={`streak-entry-${entry.rank}`}
                      onClick={() => entry.user_id ? fetchStaffPerformance(entry.user_id, entry.staff_name) : null}
                    >
                      <div className="w-10 flex justify-center">
                        {getRankIcon(entry.rank)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate hover:text-[hsl(var(--primary))] transition-colors">
                          {entry.staff_name}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Flame className="text-orange-500" size={20} />
                        <span className="text-2xl font-bold text-orange-500">
                          {entry.login_streak}
                        </span>
                        <span className="text-sm text-[hsl(var(--muted-foreground))]">
                          day{entry.login_streak === 1 ? '' : 's'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <h4 className="font-semibold text-orange-400 mb-2 flex items-center gap-2">
              <Flame size={18} />
              How streaks work
            </h4>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Your streak counts consecutive days you&apos;ve logged in. Miss a day and it resets — keep coming back to climb the ranks and unlock streak badges.
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Public Profile Popup */}
      <Dialog open={!!selectedStaff} onOpenChange={closePerformancePopup}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg" data-testid="performance-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 shrink-0 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                <span className="text-[hsl(var(--primary))] text-lg font-bold">
                  {selectedStaff?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              {selectedStaff?.name}
            </DialogTitle>
          </DialogHeader>

          {loadingPerformance ? (
            <div className="py-8 text-center text-[hsl(var(--muted-foreground))]">Loading...</div>
          ) : staffPerformance ? (
            <div className="space-y-5">
              {/* Level & XP */}
              <div className="text-center p-4 rounded-xl bg-[hsl(var(--secondary))]" data-testid="profile-level">
                <p className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">Level {staffPerformance.level?.level}</p>
                <p className="text-2xl font-bold text-[hsl(var(--primary))]">{staffPerformance.level?.title}</p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <Zap size={16} className="text-[#C36A2F]" />
                  <span className="text-lg font-bold text-[#C36A2F]" data-testid="profile-xp">{staffPerformance.total_xp} XP</span>
                </div>
                {staffPerformance.level?.next_level && (
                  <div className="mt-2">
                    <div className="h-1.5 rounded-full bg-[hsl(var(--background))] overflow-hidden">
                      <div className="h-full rounded-full bg-[hsl(var(--primary))]" style={{
                        width: `${Math.min(100, ((staffPerformance.total_xp - staffPerformance.level.min_xp) / (staffPerformance.level.next_level.min_xp - staffPerformance.level.min_xp)) * 100)}%`
                      }} />
                    </div>
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                      {staffPerformance.level.next_level.min_xp - staffPerformance.total_xp} XP to {staffPerformance.level.next_level.title}
                    </p>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))] text-center">
                  <p className="text-2xl font-bold text-[hsl(var(--primary))]">{staffPerformance.challenge_count}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Practice Sessions</p>
                </div>
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))] text-center">
                  <p className="text-2xl font-bold text-[hsl(var(--primary))]">{staffPerformance.test_count}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Tests</p>
                </div>
              </div>

              {/* Badges */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Award size={18} className="text-[#C36A2F]" />
                  Badges ({staffPerformance.badges?.length || 0})
                </h4>
                {staffPerformance.badges?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {staffPerformance.badges.map((badge, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2.5 rounded-lg bg-[hsl(var(--secondary))]" data-testid={`badge-${badge.badge_id}`}>
                        <Star size={16} className="text-[#C36A2F] shrink-0" />
                        <div>
                          <p className="text-xs font-bold">{badge.name}</p>
                          <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{badge.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-3">No badges yet</p>
                )}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-[hsl(var(--muted-foreground))]">No profile data available</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
